import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  Stat,
  StatLabel,
  StatNumber,
  Progress,
  SimpleGrid,
  HStack,
  VStack,
  Badge,
  Divider,
  Icon,
  List,
  ListItem,
  ListIcon,
  Tooltip,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Spinner,
} from "@chakra-ui/react";
import Card from "../../../../components/card/Card";
import { 
  MdCheckCircle, 
  MdAccessTime, 
  MdWarning, 
  MdPerson, 
  MdStar,
  MdStarHalf,
  MdStarOutline,
  MdFlag,
  MdHourglassEmpty,
  MdTimeline
} from "react-icons/md";
import { taskService } from "../../../../services/taskService";
import { employeeService } from "../../../../services/employeeService";

export default function EmployeeAndTaskAnalysis() {
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const bgStat = useColorModeValue("gray.50", "navy.700");
  const brandColor = useColorModeValue("brand.500", "brand.400");
  const tabBg = useColorModeValue("white", "navy.700");
  const tabSelectedBg = useColorModeValue("gray.100", "navy.800");
  const tabBorderColor = "#e45858"; // Kırmızı tab çerçevesi
  
  const [loading, setLoading] = useState(true);
  const [employeeAnalytics, setEmployeeAnalytics] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [taskData, setTaskData] = useState({
    total: 0,
    completed: 0,
    pending: 0,
    completionRate: 0,
    overdue: 0,
    employeePerformance: [],
    overdueTaskDetails: [],
    averageCompletionTime: 0,
    priorityDistribution: {
      high: 0,
      medium: 0,
      low: 0
    },
    inactiveTasks: []
  });
  
  // Performans puanına göre yıldız sayısını hesapla
  const renderStars = (rate) => {
    const stars = [];
    const fullStars = Math.floor(rate / 20);
    const halfStar = (rate % 20) >= 10;
    const emptyStars = 5 - fullStars - (halfStar ? 1 : 0);
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<Icon key={`full-${i}`} as={MdStar} color="yellow.400" boxSize="18px" />);
    }
    
    if (halfStar) {
      stars.push(<Icon key="half" as={MdStarHalf} color="yellow.400" boxSize="18px" />);
    }
    
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Icon key={`empty-${i}`} as={MdStarOutline} color="yellow.400" boxSize="18px" />);
    }
    
    return stars;
  };
  
  // Veri çekme işlemi
  useEffect(() => {
    const fetchTaskData = async () => {
      try {
        setLoading(true);
        
        // API'lerden veri çekmeyi deneyelim
        try {
          // Temel görev istatistikleri
          const stats = await taskService.getTaskStats();
          
          // Çalışan performansı
          const employeePerf = await employeeService.getEmployeeTaskPerformance();
          
          // Geciken görevler
          const overdueTasks = await employeeService.getOverdueTasks();
          
          // Öncelik dağılımı
          const priorityDist = await taskService.getTaskPriorityDistribution();
          
          // Hareketsiz görevler
          const inactiveTasks = await taskService.getInactiveTasks(15);
          
          setTaskData({
            total: stats.total || 0,
            completed: stats.completed || 0,
            pending: stats.pending || 0,
            completionRate: stats.completionRate || 0,
            overdue: overdueTasks.length || 0,
            employeePerformance: employeePerf || [],
            overdueTaskDetails: overdueTasks || [],
            averageCompletionTime: stats.averageCompletionTime || 0,
            priorityDistribution: priorityDist || { high: 0, medium: 0, low: 0 },
            inactiveTasks: inactiveTasks || []
          });
        } catch (error) {
          console.error("API'den veri çekilirken hata:", error);
          // API hatası durumunda varsayılan verileri kullan
          setTaskData({
            total: 24,
            completed: 18,
            pending: 6,
            completionRate: 75,
            overdue: 3,
            employeePerformance: [
              { name: "Ahmet Yılmaz", completed: 8, total: 10, rate: 80 },
              { name: "Ayşe Demir", completed: 6, total: 7, rate: 85 },
              { name: "Mehmet Kaya", completed: 4, total: 7, rate: 57 },
              { name: "Fatma Şahin", completed: 5, total: 8, rate: 62 },
              { name: "Ali Öztürk", completed: 7, total: 9, rate: 78 },
            ],
            overdueTaskDetails: [
              { name: "Mutfak temizliği", assignee: "Mehmet Kaya", daysOverdue: 3 },
              { name: "Stok sayımı", assignee: "Ahmet Yılmaz", daysOverdue: 2 },
              { name: "Menü güncelleme", assignee: "Ayşe Demir", daysOverdue: 5 },
              { name: "Personel eğitimi", assignee: "Fatma Şahin", daysOverdue: 4 },
              { name: "Tedarikçi görüşmesi", assignee: "Ali Öztürk", daysOverdue: 1 },
            ],
            averageCompletionTime: 2.4,
            priorityDistribution: {
              high: 5,
              medium: 12,
              low: 7
            },
            inactiveTasks: [
              { name: "Tedarikçi görüşmesi", assignee: "Ahmet Yılmaz", daysSinceLastAction: 18 },
              { name: "Personel eğitimi", assignee: "Ayşe Demir", daysSinceLastAction: 22 },
              { name: "Envanter kontrolü", assignee: "Mehmet Kaya", daysSinceLastAction: 16 },
              { name: "Müşteri anketleri", assignee: "Fatma Şahin", daysSinceLastAction: 25 },
            ]
          });
        }
      } catch (error) {
        console.error("Görev verileri getirilirken hata:", error);
        // Hata durumunda varsayılan veriler
        setTaskData({
          total: 24,
          completed: 18,
          pending: 6,
          completionRate: 75,
          overdue: 3,
          employeePerformance: [
            { name: "Ahmet Yılmaz", completed: 8, total: 10, rate: 80 },
            { name: "Ayşe Demir", completed: 6, total: 7, rate: 85 },
            { name: "Mehmet Kaya", completed: 4, total: 7, rate: 57 },
            { name: "Fatma Şahin", completed: 5, total: 8, rate: 62 },
            { name: "Ali Öztürk", completed: 7, total: 9, rate: 78 },
          ],
          overdueTaskDetails: [
            { name: "Mutfak temizliği", assignee: "Mehmet Kaya", daysOverdue: 3 },
            { name: "Stok sayımı", assignee: "Ahmet Yılmaz", daysOverdue: 2 },
            { name: "Menü güncelleme", assignee: "Ayşe Demir", daysOverdue: 5 },
            { name: "Personel eğitimi", assignee: "Fatma Şahin", daysOverdue: 4 },
            { name: "Tedarikçi görüşmesi", assignee: "Ali Öztürk", daysOverdue: 1 },
          ],
          averageCompletionTime: 2.4,
          priorityDistribution: {
            high: 5,
            medium: 12,
            low: 7
          },
          inactiveTasks: [
            { name: "Tedarikçi görüşmesi", assignee: "Ahmet Yılmaz", daysSinceLastAction: 18 },
            { name: "Personel eğitimi", assignee: "Ayşe Demir", daysSinceLastAction: 22 },
            { name: "Envanter kontrolü", assignee: "Mehmet Kaya", daysSinceLastAction: 16 },
            { name: "Müşteri anketleri", assignee: "Fatma Şahin", daysSinceLastAction: 25 },
          ]
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchTaskData();
  }, []);

  // Çalışan analizi verilerini çek
  useEffect(() => {
    const fetchEmployeeAnalytics = async () => {
      try {
        setLoadingEmployees(true);
        const data = await employeeService.getEmployeeOrderAnalytics();
        setEmployeeAnalytics(data);
      } catch (error) {
        console.error("Çalışan analizi verileri çekilirken hata:", error);
        setEmployeeAnalytics([]);
      } finally {
        setLoadingEmployees(false);
      }
    };
    
    fetchEmployeeAnalytics();
  }, []);
  
  if (loading && loadingEmployees) {
    return (
      <Card>
        <Flex justify="center" align="center" h="300px">
          <Spinner size="xl" color={brandColor} />
        </Flex>
      </Card>
    );
  }

  // Yıldız render fonksiyonu (çalışan analizi için)
  const renderRatingStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<Icon key={`full-${i}`} as={MdStar} color="yellow.400" boxSize="16px" />);
    }
    
    if (hasHalfStar) {
      stars.push(<Icon key="half" as={MdStarHalf} color="yellow.400" boxSize="16px" />);
    }
    
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Icon key={`empty-${i}`} as={MdStarOutline} color="yellow.400" boxSize="16px" />);
    }
    
    return stars;
  };

  return (
    <Card>
      <Tabs 
        variant="soft-rounded" 
        colorScheme="red" 
        borderColor={tabBorderColor}
      >
        <TabList mb={4}>
          <Tab _selected={{ bg: tabBorderColor, color: "white" }} fontSize="sm">Çalışan Analizi</Tab>
          <Tab _selected={{ bg: tabBorderColor, color: "white" }} fontSize="sm">Görev Analizi</Tab>
        </TabList>
        
        <TabPanels>
          {/* Çalışan Analizi Tab */}
          <TabPanel p={0}>
            {loadingEmployees ? (
              <Flex justify="center" align="center" h="300px">
                <Spinner size="xl" color={brandColor} />
              </Flex>
            ) : employeeAnalytics && employeeAnalytics.length > 0 ? (
              <Box>
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mb={4}>
                  {employeeAnalytics.map((employee, index) => (
                    <Box key={employee.employeeId || index} p={4} borderRadius="md" bg={bgStat}>
                      <Flex justify="space-between" align="center" mb={3}>
                        <HStack>
                          <Icon as={MdPerson} color={brandColor} boxSize="20px" />
                          <Text fontWeight="bold" fontSize="md">{employee.name}</Text>
                        </HStack>
                        {employee.averageRating > 0 && (
                          <HStack spacing={1}>
                            {renderRatingStars(employee.averageRating)}
                            <Text fontSize="xs" color="gray.500">({employee.totalRatings})</Text>
                          </HStack>
                        )}
                      </Flex>
                      
                      <VStack align="stretch" spacing={2}>
                        <Flex justify="space-between" align="center">
                          <Text fontSize="sm" color="gray.500">Sipariş Sayısı</Text>
                          <Text fontSize="md" fontWeight="bold">{employee.orderCount}</Text>
                        </Flex>
                        
                        {employee.totalTipAmount > 0 && (
                          <>
                            <Flex justify="space-between" align="center">
                              <Text fontSize="sm" color="gray.500">Toplam Bahşiş</Text>
                              <Text fontSize="md" fontWeight="bold" color="green.500">
                                ₺{employee.totalTipAmount.toLocaleString('tr-TR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}
                              </Text>
                            </Flex>
                            <Flex justify="space-between" align="center">
                              <Text fontSize="sm" color="gray.500">Ortalama Bahşiş</Text>
                              <Text fontSize="sm" color="gray.600">
                                ₺{employee.avgTipAmount.toLocaleString('tr-TR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2
                                })}
                              </Text>
                            </Flex>
                          </>
                        )}
                        
                        <Flex justify="space-between" align="center">
                          <Text fontSize="sm" color="gray.500">Toplam Gelir</Text>
                          <Text fontSize="sm" fontWeight="600" color="blue.500">
                            ₺{employee.totalRevenue.toLocaleString('tr-TR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </Text>
                        </Flex>
                      </VStack>
                    </Box>
                  ))}
                </SimpleGrid>
              </Box>
            ) : (
              <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
                Çalışan analizi verisi bulunamadı
              </Text>
            )}
          </TabPanel>
          
          {/* Görev Analizi Tab */}
          <TabPanel p={0}>
            <Flex direction="column" align="center" mb={4}>
              <Text fontSize="3xl" fontWeight="bold">
                {taskData.completionRate}%
              </Text>
              <Progress 
                value={taskData.completionRate} 
                w="100%" 
                colorScheme="brand" 
                h="8px" 
                borderRadius="md" 
                my={2}
              />
              <Flex w="100%" justify="space-between">
                <Stat bg={bgStat} p={3} borderRadius="md" textAlign="center" flex={1} mr={2}>
                  <StatLabel fontSize="xs">Toplam</StatLabel>
                  <StatNumber fontSize="lg">{taskData.total}</StatNumber>
                </Stat>
                <Stat bg={bgStat} p={3} borderRadius="md" textAlign="center" flex={1} mr={2}>
                  <StatLabel fontSize="xs">Tamamlanan</StatLabel>
                  <StatNumber fontSize="lg" color="green.500">{taskData.completed}</StatNumber>
                </Stat>
                <Stat bg={bgStat} p={3} borderRadius="md" textAlign="center" flex={1}>
                  <StatLabel fontSize="xs">Bekleyen</StatLabel>
                  <StatNumber fontSize="lg" color="orange.500">{taskData.pending}</StatNumber>
                </Stat>
              </Flex>
            </Flex>
            
            <Tabs 
              variant="soft-rounded" 
              colorScheme="red" 
              mt={4}
              borderColor={tabBorderColor}
            >
              <TabList>
                <Tab _selected={{ bg: tabBorderColor, color: "white" }} fontSize="sm">Çalışan Performansı</Tab>
                <Tab _selected={{ bg: tabBorderColor, color: "white" }} fontSize="sm">Sorunlu Görevler</Tab>
              </TabList>
        
              <TabPanels>
                <TabPanel p={4}>
                  {taskData.employeePerformance && taskData.employeePerformance.length > 0 ? (
                    <Box maxH="300px" overflowY="auto" pr={2}>
                      <SimpleGrid columns={2} spacing={3}>
                        {taskData.employeePerformance.map((employee, index) => (
                          <Box key={index} p={3} borderRadius="md" bg={bgStat}>
                            <Flex justify="space-between" align="center" mb={2}>
                              <HStack>
                                <Icon as={MdPerson} color={brandColor} boxSize="18px" />
                                <Text fontWeight="medium" fontSize="sm">{employee.name}</Text>
                              </HStack>
                              <Badge colorScheme={employee.rate >= 70 ? "green" : employee.rate >= 50 ? "orange" : "red"} fontSize="xs">
                                %{employee.rate}
                              </Badge>
                            </Flex>
                            <Flex justify="space-between" align="center">
                              <Text fontSize="xs">{employee.completed} / {employee.total} görev</Text>
                              <HStack spacing={1}>
                                {renderStars(employee.rate)}
                              </HStack>
                            </Flex>
                            <Progress 
                              value={employee.rate} 
                              size="sm" 
                              colorScheme={employee.rate >= 70 ? "green" : employee.rate >= 50 ? "orange" : "red"} 
                              mt={2}
                            />
                          </Box>
                        ))}
                      </SimpleGrid>
                    </Box>
                  ) : (
                    <Text fontSize="sm" color="gray.500" textAlign="center">Çalışan performans verisi bulunamadı</Text>
                  )}
                </TabPanel>
                
                <TabPanel p={4}>
                  <Box maxH="300px" overflowY="auto" pr={2}>
                    <SimpleGrid columns={1} spacing={4}>
                      {/* Geciken Görevler */}
                      {taskData.overdueTaskDetails && taskData.overdueTaskDetails.length > 0 && (
                        <Box>
                          <Text fontSize="sm" fontWeight="600" mb={2} color="red.500">
                            <Icon as={MdWarning} mr={1} boxSize="16px" />
                            Geciken Görevler
                          </Text>
                          
                          <List spacing={2}>
                            {taskData.overdueTaskDetails.map((task, index) => (
                              <ListItem key={index} fontSize="xs">
                                <Flex justify="space-between" align="center">
                                  <HStack>
                                    <ListIcon as={MdFlag} color="red.500" boxSize="14px" />
                                    <Text>{task.name}</Text>
                                  </HStack>
                                  <Tooltip label={`${task.assignee} - ${task.daysOverdue} gün gecikmiş`}>
                                    <Badge colorScheme="red" fontSize="xs">{task.daysOverdue} gün</Badge>
                                  </Tooltip>
                                </Flex>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                      
                      {/* Hareketsiz Görevler */}
                      {taskData.inactiveTasks && taskData.inactiveTasks.length > 0 && (
                        <Box mt={4}>
                          <Text fontSize="sm" fontWeight="600" mb={2} color="orange.500">
                            <Icon as={MdHourglassEmpty} mr={1} boxSize="16px" />
                            Hareketsiz Görevler (15+ gün)
                          </Text>
                          
                          <List spacing={2}>
                            {taskData.inactiveTasks.map((task, index) => (
                              <ListItem key={index} fontSize="xs">
                                <Flex justify="space-between" align="center">
                                  <HStack>
                                    <ListIcon as={MdAccessTime} color="orange.500" boxSize="14px" />
                                    <Text>{task.name}</Text>
                                  </HStack>
                                  <Tooltip label={`${task.assignee} - ${task.daysSinceLastAction} gündür hareketsiz`}>
                                    <Badge colorScheme="orange" fontSize="xs">{task.daysSinceLastAction} gün</Badge>
                                  </Tooltip>
                                </Flex>
                              </ListItem>
                            ))}
                          </List>
                        </Box>
                      )}
                      
                      {(!taskData.overdueTaskDetails || taskData.overdueTaskDetails.length === 0) && 
                      (!taskData.inactiveTasks || taskData.inactiveTasks.length === 0) && (
                        <Text fontSize="sm" color="gray.500" textAlign="center">Sorunlu görev bulunamadı</Text>
                      )}
                    </SimpleGrid>
                  </Box>
                </TabPanel>
              </TabPanels>
            </Tabs>
            
            <Divider my={4} />
            
            <SimpleGrid columns={2} spacing={4}>
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Ortalama Tamamlama Süresi</Text>
                <HStack>
                  <Icon as={MdTimeline} color={brandColor} boxSize={6} />
                  <Text fontSize="lg" fontWeight="bold">{taskData.averageCompletionTime} gün</Text>
                </HStack>
              </Box>
              
              <Box>
                <Text fontSize="sm" fontWeight="medium" mb={2}>Öncelik Dağılımı</Text>
                <Flex justify="space-between" align="center" w="100%">
                  <Flex direction="column" align="center" flex={1}>
                    <Badge colorScheme="red" fontSize="sm" px={3} py={1} borderRadius="md" mb={1}>
                      {taskData.priorityDistribution.high}
                    </Badge>
                    <Text fontSize="xs">Yüksek</Text>
                  </Flex>
                  <Flex direction="column" align="center" flex={1}>
                    <Badge colorScheme="orange" fontSize="sm" px={3} py={1} borderRadius="md" mb={1}>
                      {taskData.priorityDistribution.medium}
                    </Badge>
                    <Text fontSize="xs">Orta</Text>
                  </Flex>
                  <Flex direction="column" align="center" flex={1}>
                    <Badge colorScheme="blue" fontSize="sm" px={3} py={1} borderRadius="md" mb={1}>
                      {taskData.priorityDistribution.low}
                    </Badge>
                    <Text fontSize="xs">Düşük</Text>
                  </Flex>
                </Flex>
              </Box>
            </SimpleGrid>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Card>
  );
} 